'use client';

/**
 * TCO Referral Create Form Component
 *
 * Form for creating new service referrals in the TCO (care organization) context.
 * Supports all TCO-specific source types and integrates with care plans.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  FileText,
  User,
  Search,
  Loader2,
  AlertCircle,
  Plus,
  Heart,
  ClipboardList,
  UserCheck,
  Shield,
  Bot,
  Home,
  Utensils,
  Car,
  Briefcase,
  CheckCircle,
  X,
} from 'lucide-react';
import {
  tcoServiceReferralsAPI,
  TCOServiceReferralCreate,
  TCOSourceType,
  UrgencyLevel,
  SDOHDomainCode,
} from '@/services/api/tcoServiceReferrals';
import { apiClient } from '@/services/apiClient';
import { useToast } from '@/components/ui/use-toast';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface TCOReferralCreateFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (referralId: number) => void;
  /** Pre-selected member ID */
  memberId?: number;
  /** Pre-selected member name */
  memberName?: string;
  /** Pre-selected care plan ID */
  carePlanId?: number;
  /** Pre-selected care plan goal ID */
  carePlanGoalId?: number;
  /** Pre-selected source type */
  defaultSourceType?: TCOSourceType;
  /** Pre-selected SDOH domains */
  defaultSdohDomains?: SDOHDomainCode[];
  className?: string;
}

// Source type options with icons and descriptions
const SOURCE_TYPE_OPTIONS: {
  value: TCOSourceType;
  label: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    value: 'care_plan_goal',
    label: 'Care Plan Goal',
    description: 'Referral based on a care plan goal or intervention',
    icon: <ClipboardList className="w-4 h-4" />,
  },
  {
    value: 'sdoh_screening',
    label: 'SDOH Screening',
    description: 'Referral based on social determinants of health screening results',
    icon: <Heart className="w-4 h-4" />,
  },
  {
    value: 'navigator_referral',
    label: 'Navigator Referral',
    description: 'Referral initiated by care navigator or CHW',
    icon: <UserCheck className="w-4 h-4" />,
  },
  {
    value: 'member_request',
    label: 'Member Request',
    description: 'Member self-initiated request for services',
    icon: <User className="w-4 h-4" />,
  },
  {
    value: 'pcp_referral',
    label: 'PCP Referral',
    description: 'Referral from primary care provider',
    icon: <Shield className="w-4 h-4" />,
  },
  {
    value: 'discharge_planning',
    label: 'Discharge Planning',
    description: 'Hospital or facility discharge planning referral',
    icon: <FileText className="w-4 h-4" />,
  },
  {
    value: 'case_review',
    label: 'Case Review',
    description: 'Referral from case review or care huddle',
    icon: <ClipboardList className="w-4 h-4" />,
  },
  {
    value: 'ai_recommended',
    label: 'AI Recommended',
    description: 'AI-driven recommendation based on member data',
    icon: <Bot className="w-4 h-4" />,
  },
];

// SDOH domain options with icons - values must match backend SDOHDomain enum
const SDOH_DOMAIN_OPTIONS: {
  value: SDOHDomainCode;
  label: string;
  icon: React.ReactNode;
}[] = [
  { value: 'HOUSING', label: 'Housing', icon: <Home className="w-4 h-4" /> },
  { value: 'FOOD_NUTRITION', label: 'Food Security', icon: <Utensils className="w-4 h-4" /> },
  { value: 'TRANSPORTATION', label: 'Transportation', icon: <Car className="w-4 h-4" /> },
  { value: 'ECONOMIC_STABILITY', label: 'Economic Stability', icon: <Briefcase className="w-4 h-4" /> },
  { value: 'EDUCATION_EMPLOYMENT', label: 'Education & Employment', icon: <FileText className="w-4 h-4" /> },
  { value: 'SOCIAL_SUPPORT', label: 'Social Support', icon: <Heart className="w-4 h-4" /> },
  { value: 'HEALTH_BEHAVIORS', label: 'Health Behaviors', icon: <Shield className="w-4 h-4" /> },
  { value: 'SAFETY', label: 'Safety', icon: <AlertCircle className="w-4 h-4" /> },
];

// Urgency options - values must match backend UrgencyLevel enum
const URGENCY_OPTIONS: { value: UrgencyLevel; label: string; description: string }[] = [
  { value: 'routine', label: 'Routine', description: 'Standard processing time' },
  { value: 'urgent', label: 'Urgent', description: 'Needs attention within 24-48 hours' },
  { value: 'emergent', label: 'Emergent', description: 'Immediate action required' },
];

// ============================================================================
// COMPONENT
// ============================================================================

export function TCOReferralCreateForm({
  open,
  onClose,
  onSuccess,
  memberId: propMemberId,
  memberName: propMemberName,
  carePlanId: propCarePlanId,
  carePlanGoalId: propCarePlanGoalId,
  defaultSourceType,
  defaultSdohDomains,
  className = '',
}: TCOReferralCreateFormProps) {
  const { toast } = useToast();
  const { t } = useAppTranslation();

  // Form state
  const [activeTab, setActiveTab] = useState<'member' | 'service' | 'details'>('member');
  const [submitting, setSubmitting] = useState(false);

  // Member selection
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState<number | undefined>(propMemberId);
  const [selectedMemberName, setSelectedMemberName] = useState(propMemberName || '');
  const [searchingMembers, setSearchingMembers] = useState(false);
  const [memberSearchResults, setMemberSearchResults] = useState<{ id: number; name: string }[]>([]);

  // Service configuration
  const [serviceTypes, setServiceTypes] = useState<{ code: string; name: string; category_name?: string; domain_name?: string }[]>([]);
  const [selectedServiceType, setSelectedServiceType] = useState('');
  const [loadingServiceTypes, setLoadingServiceTypes] = useState(true);
  const [sourceType, setSourceType] = useState<TCOSourceType>(defaultSourceType || 'navigator_referral');
  const [urgency, setUrgency] = useState<UrgencyLevel>('routine');
  const [selectedSdohDomains, setSelectedSdohDomains] = useState<SDOHDomainCode[]>(defaultSdohDomains || []);

  // Details
  const [carePlanId, setCarePlanId] = useState<number | undefined>(propCarePlanId);
  const [carePlanGoalId, setCarePlanGoalId] = useState<number | undefined>(propCarePlanGoalId);
  const [serviceDescription, setServiceDescription] = useState('');
  const [serviceNotes, setServiceNotes] = useState('');
  const [triggerMatching, setTriggerMatching] = useState(true);

  // Load service types on mount
  useEffect(() => {
    const loadServiceTypes = async () => {
      setLoadingServiceTypes(true);
      try {
        // Use the shared apiClient to get service types from taxonomy
        const response = await apiClient.get('/api/service-referrals/taxonomy/types');
        const types = response.data || [];
        setServiceTypes(types.map((t: any) => ({
          code: t.code,
          name: t.name,
          category_name: t.category_name,
          domain_name: t.domain_name,
        })));
      } catch (err) {
        console.error('Failed to load service types:', err);
      } finally {
        setLoadingServiceTypes(false);
      }
    };
    loadServiceTypes();
  }, []);

  // Search members
  const handleMemberSearch = async () => {
    if (!memberSearchQuery.trim()) return;

    setSearchingMembers(true);
    try {
      // Use the shared apiClient to search members
      const response = await apiClient.get('/api/enrollments/search-members', {
        params: {
          search: memberSearchQuery,
          limit: 10,
        },
      });
      const results = response.data || {};
      setMemberSearchResults(
        (results.members || []).map((m: any) => ({
          id: m.id,
          name: `${m.first_name} ${m.last_name}`,
        }))
      );
    } catch (err) {
      console.error('Failed to search members:', err);
    } finally {
      setSearchingMembers(false);
    }
  };

  // Select member
  const handleSelectMember = (member: { id: number; name: string }) => {
    setSelectedMemberId(member.id);
    setSelectedMemberName(member.name);
    setMemberSearchResults([]);
    setMemberSearchQuery('');
  };

  // Toggle SDOH domain
  const toggleSdohDomain = (domain: SDOHDomainCode) => {
    setSelectedSdohDomains((prev) =>
      prev.includes(domain) ? prev.filter((d) => d !== domain) : [...prev, domain]
    );
  };

  // Validate form
  const isValid = selectedMemberId && selectedServiceType && sourceType;

  // Handle submit
  const handleSubmit = async () => {
    if (!isValid) {
      toast({
        title: t('tcoReferrals.createForm.validationError', 'Validation Error'),
        description: t('tcoReferrals.createForm.fillRequiredFields', 'Please fill in all required fields'),
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const referralData: TCOServiceReferralCreate = {
        member_id: selectedMemberId!,
        service_type_code: selectedServiceType,
        source_type: sourceType,
        urgency_level: urgency,
        service_description: serviceDescription || undefined,
        clinical_notes: serviceNotes || undefined,
        care_plan_id: carePlanId,
        care_plan_goal_id: carePlanGoalId,
        sdoh_needs: selectedSdohDomains.length > 0 ? selectedSdohDomains : undefined,
        auto_match: triggerMatching,
      };

      const result = await tcoServiceReferralsAPI.create(referralData);

      toast({
        title: t('tcoReferrals.createForm.referralCreated', 'Referral Created'),
        description: t('tcoReferrals.createForm.referralCreatedDesc', 'Care coordination referral #{{id}} created successfully.', { id: result.id }),
      });

      onSuccess?.(result.id);
      handleClose();
    } catch (err: any) {
      console.error('Error creating TCO referral:', err);
      toast({
        title: t('tcoReferrals.createForm.error', 'Error'),
        description: err.response?.data?.detail || t('tcoReferrals.createForm.failedToCreate', 'Failed to create referral'),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form and close
  const handleClose = () => {
    setActiveTab('member');
    setSelectedMemberId(propMemberId);
    setSelectedMemberName(propMemberName || '');
    setMemberSearchQuery('');
    setMemberSearchResults([]);
    setSelectedServiceType('');
    setSourceType(defaultSourceType || 'navigator_referral');
    setUrgency('routine');
    setSelectedSdohDomains(defaultSdohDomains || []);
    setCarePlanId(propCarePlanId);
    setCarePlanGoalId(propCarePlanGoalId);
    setServiceDescription('');
    setServiceNotes('');
    setTriggerMatching(true);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${className}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-indigo-600" />
            {t('tcoReferrals.createForm.title', 'Create Care Coordination Referral')}
          </DialogTitle>
          <DialogDescription>
            {t('tcoReferrals.createForm.description', 'Create a new service referral for member care coordination.')}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="member" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              {t('tcoReferrals.createForm.tabs.member', 'Member')}
            </TabsTrigger>
            <TabsTrigger
              value="service"
              className="flex items-center gap-2"
              disabled={!selectedMemberId}
            >
              <FileText className="h-4 w-4" />
              {t('tcoReferrals.createForm.tabs.service', 'Service')}
            </TabsTrigger>
            <TabsTrigger
              value="details"
              className="flex items-center gap-2"
              disabled={!selectedServiceType}
            >
              <ClipboardList className="h-4 w-4" />
              {t('tcoReferrals.createForm.tabs.details', 'Details')}
            </TabsTrigger>
          </TabsList>

          {/* Member Selection Tab */}
          <TabsContent value="member" className="space-y-4 mt-4">
            {selectedMemberId ? (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-full">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-green-900">{selectedMemberName}</p>
                        <p className="text-sm text-green-700">{t('tcoReferrals.createForm.memberId', 'Member ID')}: {selectedMemberId}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedMemberId(undefined);
                        setSelectedMemberName('');
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('tcoReferrals.createForm.searchMember', 'Search Member')} *</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder={t('tcoReferrals.createForm.searchPlaceholder', 'Search by name or member ID...')}
                        value={memberSearchQuery}
                        onChange={(e) => setMemberSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleMemberSearch()}
                        className="pl-10"
                      />
                    </div>
                    <Button onClick={handleMemberSearch} disabled={searchingMembers}>
                      {searchingMembers ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        t('tcoReferrals.createForm.search', 'Search')
                      )}
                    </Button>
                  </div>
                </div>

                {memberSearchResults.length > 0 && (
                  <div className="border rounded-md divide-y max-h-48 overflow-y-auto">
                    {memberSearchResults.map((member) => (
                      <button
                        key={member.id}
                        onClick={() => handleSelectMember(member)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3"
                      >
                        <User className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{member.name}</p>
                          <p className="text-sm text-gray-500">{t('tcoReferrals.createForm.id', 'ID')}: {member.id}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {selectedMemberId && (
              <div className="flex justify-end">
                <Button onClick={() => setActiveTab('service')}>
                  {t('tcoReferrals.createForm.continueToService', 'Continue to Service Selection')}
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Service Configuration Tab */}
          <TabsContent value="service" className="space-y-4 mt-4">
            {/* Service Type */}
            <div className="space-y-2">
              <Label>{t('tcoReferrals.createForm.serviceType', 'Service Type')} *</Label>
              {loadingServiceTypes ? (
                <div className="flex items-center gap-2 p-3 border rounded-md bg-gray-50">
                  <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                  <span className="text-sm text-gray-500">{t('tcoReferrals.createForm.loadingServiceTypes', 'Loading service types...')}</span>
                </div>
              ) : serviceTypes.length === 0 ? (
                <div className="flex items-center gap-2 p-3 border rounded-md bg-amber-50 border-amber-200">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  <span className="text-sm text-amber-700">{t('tcoReferrals.createForm.noServiceTypes', 'No service types available. Please contact support.')}</span>
                </div>
              ) : (
                <Select value={selectedServiceType} onValueChange={setSelectedServiceType}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('tcoReferrals.createForm.selectServiceType', 'Select service type...')} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {serviceTypes.map((st) => (
                      <SelectItem key={st.code} value={st.code}>
                        <div className="flex flex-col">
                          <span>{st.name}</span>
                          {st.category_name && (
                            <span className="text-xs text-gray-500">{st.category_name}</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Source Type */}
            <div className="space-y-2">
              <Label>{t('tcoReferrals.createForm.referralSource', 'Referral Source')} *</Label>
              <div className="grid grid-cols-2 gap-2">
                {SOURCE_TYPE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSourceType(option.value)}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      sourceType === option.value
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={sourceType === option.value ? 'text-indigo-600' : 'text-gray-500'}
                      >
                        {option.icon}
                      </span>
                      <span
                        className={`font-medium ${
                          sourceType === option.value ? 'text-indigo-900' : 'text-gray-900'
                        }`}
                      >
                        {option.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">{option.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Urgency */}
            <div className="space-y-2">
              <Label>{t('tcoReferrals.createForm.urgencyLevel', 'Urgency Level')}</Label>
              <div className="flex gap-2">
                {URGENCY_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setUrgency(option.value)}
                    className={`flex-1 p-3 rounded-lg border text-center transition-colors ${
                      urgency === option.value
                        ? option.value === 'emergency'
                          ? 'border-red-500 bg-red-50'
                          : option.value === 'urgent'
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p
                      className={`font-medium ${
                        urgency === option.value
                          ? option.value === 'emergency'
                            ? 'text-red-900'
                            : option.value === 'urgent'
                            ? 'text-orange-900'
                            : 'text-green-900'
                          : 'text-gray-900'
                      }`}
                    >
                      {option.label}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{option.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* SDOH Domains */}
            <div className="space-y-2">
              <Label>{t('tcoReferrals.createForm.sdohDomains', 'SDOH Domains (Optional)')}</Label>
              <p className="text-sm text-gray-500">
                {t('tcoReferrals.createForm.sdohDomainsDesc', 'Select social determinants of health domains relevant to this referral')}
              </p>
              <div className="flex flex-wrap gap-2">
                {SDOH_DOMAIN_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => toggleSdohDomain(option.value)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full border text-sm transition-colors ${
                      selectedSdohDomains.includes(option.value)
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {option.icon}
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {selectedServiceType && (
              <div className="flex justify-end">
                <Button onClick={() => setActiveTab('details')}>{t('tcoReferrals.createForm.continueToDetails', 'Continue to Details')}</Button>
              </div>
            )}
          </TabsContent>

          {/* Details Tab */}
          <TabsContent value="details" className="space-y-4 mt-4">
            {/* Care Plan Links */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('tcoReferrals.createForm.carePlanId', 'Care Plan ID (Optional)')}</Label>
                <Input
                  type="number"
                  placeholder={t('tcoReferrals.createForm.enterCarePlanId', 'Enter care plan ID...')}
                  value={carePlanId || ''}
                  onChange={(e) => setCarePlanId(e.target.value ? parseInt(e.target.value) : undefined)}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('tcoReferrals.createForm.carePlanGoalId', 'Care Plan Goal ID (Optional)')}</Label>
                <Input
                  type="number"
                  placeholder={t('tcoReferrals.createForm.enterGoalId', 'Enter goal ID...')}
                  value={carePlanGoalId || ''}
                  onChange={(e) =>
                    setCarePlanGoalId(e.target.value ? parseInt(e.target.value) : undefined)
                  }
                />
              </div>
            </div>

            {/* Service Description */}
            <div className="space-y-2">
              <Label>{t('tcoReferrals.createForm.serviceDescription', 'Service Description')}</Label>
              <Textarea
                placeholder={t('tcoReferrals.createForm.describeServices', 'Describe the services needed...')}
                value={serviceDescription}
                onChange={(e) => setServiceDescription(e.target.value)}
                rows={3}
              />
            </div>

            {/* Service Notes */}
            <div className="space-y-2">
              <Label>{t('tcoReferrals.createForm.internalNotes', 'Internal Notes')}</Label>
              <Textarea
                placeholder={t('tcoReferrals.createForm.addInternalNotes', 'Add any internal notes for care team...')}
                value={serviceNotes}
                onChange={(e) => setServiceNotes(e.target.value)}
                rows={2}
              />
            </div>

            {/* Auto-match Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">{t('tcoReferrals.createForm.autoMatchProviders', 'Auto-match Providers')}</p>
                <p className="text-sm text-gray-500">
                  {t('tcoReferrals.createForm.autoMatchProvidersDesc', 'Automatically find and match available providers')}
                </p>
              </div>
              <Switch checked={triggerMatching} onCheckedChange={setTriggerMatching} />
            </div>

            {/* Summary */}
            <Card className="bg-indigo-50 border-indigo-200">
              <CardContent className="p-4">
                <h4 className="font-medium text-indigo-900 mb-2">{t('tcoReferrals.createForm.referralSummary', 'Referral Summary')}</h4>
                <div className="space-y-1 text-sm">
                  <p>
                    <span className="text-indigo-700">{t('tcoReferrals.createForm.member', 'Member')}:</span> {selectedMemberName}
                  </p>
                  <p>
                    <span className="text-indigo-700">{t('tcoReferrals.createForm.service', 'Service')}:</span>{' '}
                    {serviceTypes.find((st) => st.code === selectedServiceType)?.name || selectedServiceType}
                  </p>
                  <p>
                    <span className="text-indigo-700">{t('tcoReferrals.createForm.source', 'Source')}:</span>{' '}
                    {SOURCE_TYPE_OPTIONS.find((o) => o.value === sourceType)?.label}
                  </p>
                  <p>
                    <span className="text-indigo-700">{t('tcoReferrals.createForm.urgency', 'Urgency')}:</span>{' '}
                    {URGENCY_OPTIONS.find((o) => o.value === urgency)?.label}
                  </p>
                  {selectedSdohDomains.length > 0 && (
                    <p>
                      <span className="text-indigo-700">{t('tcoReferrals.createForm.sdohDomainsLabel', 'SDOH Domains')}:</span>{' '}
                      {selectedSdohDomains.join(', ')}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={handleClose}>
            {t('tcoReferrals.createForm.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || submitting}>
            {submitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            {t('tcoReferrals.createForm.createReferral', 'Create Referral')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TCOReferralCreateForm;
